import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from '@sentry/react';
import { isExpectedClientTransportError } from '@core/sentry/utils';
import { useBooking } from "@features/booking/state";
import { useBookingFlow, isPaymentGuarantee, shouldShowExperience } from "@features/booking/flow";
import { useAuth } from "@core/auth/AuthProvider";
import { COMPLETE_PROFILE_PATH } from "@core/auth";
import { redirectToAuth } from "@core/auth/authRedirect";
import { useRestaurant } from "@core/tenant/TenantProvider";
import { useTimeSlots, timeSlotsKeys } from "./useTimeSlots";
import { useCalendarAvailability } from "./useCalendarAvailability";
import { usePartnerAvailability } from "./usePartnerAvailability";
import { checkUserLimitReached } from "@core/auth/authApi";
import { MOCK_EXPERIENCES } from "@features/booking/constants";
import { PUBLIC_SEARCH_MODE } from "@shared/types/restaurant";
import type { ServiceType } from "@features/booking/state/types";
import { CALENDAR_DAY_TYPE_REQUEST, type MergedCalendarDay } from "@features/booking/types/calendar";
import type { PartnerSuggestion } from "@features/booking/components/PartnerRestaurantSuggestions";

/**
 * TimeSlot type for UI display
 */
export type TimeSlot = {
  time: string;
  isPremium?: boolean;
  deposit?: boolean;
  /** Original slot type: 'deposit' | 'no_show' | 'Yes' etc. */
  type?: string;
  /** True when any enabled menu in the slot has eventMenu flag */
  hasEventMenu?: boolean;
};

// Re-export from constants for backward compatibility
export const EXPERIENCES = MOCK_EXPERIENCES;

export type LimitWarningType = 'daily' | 'weekly' | 'monthly' | 'blocked';

export type LimitWarning = {
  open: boolean;
  type: LimitWarningType;
  limit: number;
  contactEmail: string;
};

export type CrossServiceAlternative = {
  service: ServiceType;
  slots: TimeSlot[];
};

type UseSelectTimeStateReturn = {
  showExperience: boolean;
  selectedExp: string;
  setSelectedExp: (id: string) => void;
  selectedTime: string | null;
  setSelectedTime: (t: string | null) => void;
  loading: boolean;
  EXPERIENCES: typeof EXPERIENCES;
  availableTimes: TimeSlot[];
  handleSelectTime: (time: string) => void;
  handleBack: () => void;
  getSelectedExperienceLabel: () => string;
  handleJoinWaitingList: () => void;
  columnType: string | null;
  allowWaitingList: boolean;
  isWalkInDay: boolean;
  isWaitingListDay: boolean;
  centerHasOnlyPreferred: boolean;
  isAuthenticated: boolean;
  handlePreferredLogin: () => void;
  limitWarning: LimitWarning | null;
  closeLimitWarning: () => void;
  crossServiceAlternatives: CrossServiceAlternative[];
  handleSelectCrossServiceSlot: (service: ServiceType, time: string) => void;
  calendarAvailability: MergedCalendarDay[];
  isCalendarLoading: boolean;
  handleCalendarDateSelect: (date: Date, service: ServiceType) => void;
  showNoAvailabilityCalendar: boolean;
  calendarServiceTypes: ServiceType[];
  currentService: ServiceType | null;
  partnerSuggestions: PartnerSuggestion[];
};

/**
 * Hook for managing the SelectTime step state and navigation
 *
 * Uses booking context for state and timeSlots from the API
 * (loaded in SelectServicePage when user selects a service)
 */
export function useSelectTimeState(): UseSelectTimeStateReturn {
  const { goToNext, goToPrevious, goToStep, activeSteps } = useBookingFlow();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const restaurant = useRestaurant();

  // Get booking state from context
  const booking = useBooking();
  const {
    diners,
    selectedDate,
    service,
    timeSlots,
    showExperience,
    selectedExperience: selectedExperienceFromState,
    setSelectedTime: setBookingSelectedTime,
    setSelectedExperience,
    setSelectedArea: setBookingSelectedArea,
    setTimeSlots: setBookingTimeSlots,
    setSlotTerms: setBookingSlotTerms,
    setColumnType: setBookingColumnType,
    setService: setBookingService,
    updateBooking,
    meitreNetworkMenuId,
  } = booking;

  // --- Reset menu filter to original pre-selected menu on mount ---
  // When the user navigates back to /select-time after selecting an alternative,
  // restore the filter to the original pre-selected menu so alternatives can be
  // shown again if the original menu still has no availability.
  useEffect(() => {
    if (booking.preSelectedMenuId) {
      updateBooking({ selectedMenuFilter: [booking.preSelectedMenuId] });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Auto-set service when SERVICE step is skipped (single-service restaurant) ---
  // SelectServicePage never renders so setService() is never called.
  const effectiveService = service ?? (
    (restaurant.serviceTypes?.length === 1) ? (restaurant.serviceTypes[0] as ServiceType) : null
  );
  useEffect(() => {
    if (!service && effectiveService) {
      setBookingService(effectiveService);
    }
  }, [service, effectiveService, setBookingService]);

  // --- Self-load time slots when SERVICE step was skipped ---
  // Only self-load when timeSlots is null (SERVICE step was skipped).
  // When SelectServicePage sets timeSlots to [] (no slots), don't re-load.
  const needsDirectLoad = !timeSlots && !!effectiveService && !!selectedDate;
  const directServiceTypes = useMemo(
    () => (effectiveService ? [effectiveService] : []),
    [effectiveService],
  );
  const directDate = selectedDate;

  const {
    slotsByService: directSlotsByService,
    getServiceSlots: directGetServiceSlots,
    isLoading: isDirectSlotsLoading,
  } = useTimeSlots({
    restaurantId: restaurant.id,
    partySize: diners,
    date: directDate,
    serviceTypes: directServiceTypes,
    enabled: needsDirectLoad,
    hasPreferredAccess: !!authState.preferred,
    meitreNetworkMenuId: meitreNetworkMenuId,
    areaFilterIds: booking.areaFilterIds,
  });

  // --- Cross-service alternatives (fetch slots for ALL services) ---
  const allServiceTypes = useMemo(
    () => (restaurant.serviceTypes || []) as ServiceType[],
    [restaurant.serviceTypes],
  );

  const {
    slotsByService: allSlotsByService,
    getServiceSlots: allGetServiceSlots,
    isLoading: isAllSlotsLoading,
  } = useTimeSlots({
    restaurantId: restaurant.id,
    partySize: diners,
    date: directDate,
    serviceTypes: allServiceTypes,
    enabled: !!directDate && allServiceTypes.length > 1 && diners > 0,
    hasPreferredAccess: !!authState.preferred,
    meitreNetworkMenuId: meitreNetworkMenuId,
    areaFilterIds: booking.areaFilterIds,
  });

  // Stable ref for activeSteps to avoid recreating handleCalendarDateSelect on step changes
  const activeStepsRef = useRef(activeSteps);
  activeStepsRef.current = activeSteps;

  // --- Calendar availability (cache hit from SelectDatePage) ---
  const {
    mergedCalendar: calendarAvailability,
    isLoading: isCalendarLoading,
    getDateAvailability,
  } = useCalendarAvailability({
    restaurantId: restaurant.id,
    partySize: diners,
    serviceTypes: allServiceTypes,
    enabled: diners > 0,
    meitreNetworkMenuId: meitreNetworkMenuId,
    preSelectedMenuId: booking.preSelectedMenuId,
    hasPreferredAccess: !!authState.preferred,
  });

  // Store direct-loaded slots into booking state (only after fresh data arrives, not stale cache)
  useEffect(() => {
    if (!needsDirectLoad || !effectiveService || isDirectSlotsLoading) return;
    const slots = directGetServiceSlots(effectiveService);
    const response = directSlotsByService[effectiveService];
    if (!response) return;

    // Detect preferred-only column before checking slot count
    const rawSlots = response.center?.slots ?? [];
    const hasPreferred = response.center?.hasPreferred;
    const thereArePreferred = rawSlots.some(s => s.preferred && s.type !== 'No');
    const thereAreNormal = rawSlots.some(s => !s.preferred && s.type !== 'No');
    // Refine isEarlyAccessFlow using per-service calendar day type.
    // Only SET to true if calendar confirms "request" — never reset to false here,
    // because the flag may have been set correctly by a previous step (SelectDatePage
    // or SelectServicePage) and the calendar data might not be loaded yet.
    const directDayAvail = selectedDate ? getDateAvailability(selectedDate) : null;
    const directServiceType = directDayAvail?.services?.[effectiveService]?.type;
    const calendarSaysRequest = directServiceType === CALENDAR_DAY_TYPE_REQUEST;
    updateBooking({
      centerHasOnlyPreferred: !!hasPreferred && thereArePreferred && !thereAreNormal,
      ...(calendarSaysRequest ? { isEarlyAccessFlow: true } : {}),
    });

    setBookingTimeSlots(slots.length > 0 ? slots : []);
    setBookingSlotTerms(response.center?.termsAndConditions ?? []);
    setBookingColumnType(response.center?.type ?? null);
  }, [needsDirectLoad, effectiveService, isDirectSlotsLoading, directGetServiceSlots, directSlotsByService, setBookingTimeSlots, setBookingSlotTerms, setBookingColumnType, updateBooking, selectedDate, getDateAvailability]);

  // Handler: user selects a date from the no-availability calendar.
  // The calendar already shows per-service data, so we respect that service selection
  // and skip the SERVICE step — go directly to TIME (same as legacy behavior).
  const handleCalendarDateSelect = useCallback((date: Date, calendarService: ServiceType) => {
    booking.setSelectedDate(format(date, 'yyyy-MM-dd'));
    setBookingService(calendarService);
    // Clear stale time slots so TIME page self-loads fresh data for the new date+service
    const dateKey = format(date, 'yyyy-MM-dd');
    const dayAvail = getDateAvailability(dateKey);
    // Use service-specific calendar day type for early-access detection
    const serviceDayType = dayAvail?.services?.[calendarService]?.type;
    updateBooking({
      timeSlots: null,
      isSelectedDateSpecial: dayAvail?.isSpecialDay ?? false,
      selectedCalendarDayType: dayAvail?.type ?? null,
      isEarlyAccessFlow: serviceDayType === CALENDAR_DAY_TYPE_REQUEST,
      // Exit pre-selected menu flow — the user is choosing a new date/service
      // from the calendar, so the original menu shortcut no longer applies.
      preSelectedMenuId: null,
      selectedMenuFilter: null,
    });
    // Navigate to TIME step directly, skipping SERVICE
    const steps = activeStepsRef.current;
    const timeStepIndex = steps.findIndex(s => s.id === 'TIME');
    if (timeStepIndex >= 0) {
      goToStep('TIME');
    } else {
      // Fallback: go to step after DATE
      const dateStepIndex = steps.findIndex(s => s.id === 'DATE');
      if (dateStepIndex >= 0 && dateStepIndex < steps.length - 1) {
        goToStep(steps[dateStepIndex + 1].id);
      } else {
        goToStep('DATE');
      }
    }
  }, [booking.setSelectedDate, setBookingService, updateBooking, goToStep, getDateAvailability]);

  // --- Partner restaurant suggestions (cross-restaurant fallback) ---
  const isSpecialFlow = !!booking.specialEvent;
  const hasPartners = (restaurant.partners?.length ?? 0) > 0;
  // timeSlots is null when not yet loaded, [] when loaded but empty
  const slotsLoadedEmpty = Array.isArray(timeSlots) && timeSlots.length === 0 && !isDirectSlotsLoading;

  const { data: partnerAvailabilityData } = usePartnerAvailability({
    restaurantId: restaurant.id,
    partySize: diners,
    serviceType: effectiveService,
    date: directDate,
    hasNoSlots: slotsLoadedEmpty,
    featureEnabled: !!restaurant.offerCrossAvailabilityFromPartners,
    hasPartners,
    isSpecialFlow,
  });

  // Limit warning state
  const [limitWarning, setLimitWarning] = useState<LimitWarning | null>(null);
  const closeLimitWarning = useCallback(() => {
    setLimitWarning(null);
    // Invalidate cached time slots so the next render fetches fresh data
    // from the API (the limit markers may no longer apply after user
    // cancels the conflicting reservation).
    queryClient.invalidateQueries({ queryKey: timeSlotsKeys.all });
    // Clear booking-state slots so the direct-load useEffect re-runs
    setBookingTimeSlots(null);
  }, [queryClient, setBookingTimeSlots]);

  // Track auth state changes for post-auth limit check
  const prevAuthRef = useRef(authState.isAuthenticated);

  // Local state for selections
  const initialExperience = selectedExperienceFromState?.id || "exp1";
  const [selectedExp, setSelectedExp] = useState(initialExperience);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  // Show loading skeleton when experience changes or when loading slots directly
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [selectedExp]);

  // Deep-link auto-select: when deepLinkCenterHour is set and slots are loaded,
  // auto-select the matching time slot and advance the flow.
  useEffect(() => {
    const centerHour = booking.deepLinkCenterHour;
    if (!centerHour || !timeSlots || timeSlots.length === 0) return;

    const matchingSlot = timeSlots.find((s) => {
      // Match HH:mm format (normalize both to HH:mm)
      const slotHour = s.hour.split(':').slice(0, 2).join(':');
      return slotHour === centerHour;
    });

    if (matchingSlot) {
      // Clear the deep-link param so it doesn't re-trigger
      updateBooking({ deepLinkCenterHour: null });
      // Use a micro-delay to let the UI render before navigating
      setTimeout(() => handleSelectTime(matchingSlot.hour), 100);
    } else {
      // Slot not found — clear param and let user pick manually
      updateBooking({ deepLinkCenterHour: null });
    }
  }, [booking.deepLinkCenterHour, timeSlots]); // eslint-disable-line react-hooks/exhaustive-deps

  // Post-auth limit check: when user authenticates during the booking flow,
  // call /get-user-has-reach-limit to see if they already hit daily/weekly limits.
  useEffect(() => {
    const wasAuthenticated = prevAuthRef.current;
    prevAuthRef.current = authState.isAuthenticated;

    if (!wasAuthenticated && authState.isAuthenticated && selectedDate) {
      checkUserLimitReached(restaurant.id, selectedDate)
        .then((result) => {
          if (result.dailyReachLimit) {
            setLimitWarning({
              open: true,
              type: 'daily',
              limit: restaurant.dailyLimit,
              contactEmail: restaurant.contactEmail,
            });
          } else if (result.weeklyReachLimit) {
            setLimitWarning({
              open: true,
              type: 'weekly',
              limit: restaurant.weeklyLimit,
              contactEmail: restaurant.contactEmail,
            });
          }
        })
        .catch((err) => {
          if (isExpectedClientTransportError(err)) return;
          Sentry.captureException(err, { tags: { source: 'user-limit-check' } });
        });
    }
  }, [authState.isAuthenticated, selectedDate, restaurant]);

  /**
   * When user selects a time, save to booking state and navigate to next step.
   * If the slot has exactly one menu, auto-assign menu and area
   * (replicates legacy behavior where these come from the menu object).
   *
   * Before advancing, checks:
   * 1. Blocked user → blocked modal
   * 2. Slot type *-limit-reached → limit modal
   * 3. Otherwise → proceed
   */
  const handleSelectTime = useCallback(
    (time: string) => {
      // Check if user is blocked in this restaurant
      if (
        authState.isAuthenticated &&
        authState.blockedInRestaurantsId?.includes(String(restaurant.id))
      ) {
        setLimitWarning({
          open: true,
          type: 'blocked',
          limit: 0,
          contactEmail: restaurant.contactEmail,
        });
        return;
      }

      // Check slot-level limit types
      const slot = timeSlots?.find((s) => s.hour === time);
      if (slot?.type === 'daily-limit-reached') {
        setLimitWarning({
          open: true,
          type: 'daily',
          limit: restaurant.dailyLimit,
          contactEmail: restaurant.contactEmail,
        });
        return;
      }
      if (slot?.type === 'weekly-limit-reached') {
        setLimitWarning({
          open: true,
          type: 'weekly',
          limit: restaurant.weeklyLimit,
          contactEmail: restaurant.contactEmail,
        });
        return;
      }
      if (slot?.type === 'monthly-limit-reached') {
        setLimitWarning({
          open: true,
          type: 'monthly',
          limit: restaurant.monthlyLimit,
          contactEmail: restaurant.contactEmail,
        });
        return;
      }

      setSelectedTime(time);
      setBookingSelectedTime(time);

      // Auto-assign menu + area from the slot's enabled menus.
      // When a menu filter is active, restrict to matching menus so the
      // auto-assignment picks the user's intended menu, not just the first one.
      const allEnabledMenus = slot?.menus?.filter((m) => !m.disabled) ?? [];
      const activeFilter = booking.selectedMenuFilter;
      const filterSet = activeFilter && activeFilter.length > 0
        ? new Set(activeFilter)
        : null;
      const enabledMenus = filterSet
        ? allEnabledMenus.filter((m) => filterSet.has(m.id))
        : allEnabledMenus;

      // Area routing (Flow C/D): don't auto-assign from menu.area. Use the
      // slot's availableAreas, optionally intersected with areaFilterIds (Flow D).
      const isAreaMode = restaurant.publicSearchMode === PUBLIC_SEARCH_MODE.AREA;
      const slotAreas = slot?.availableAreas ?? [];
      const areaFilter = booking.areaFilterIds;
      const filteredSlotAreas = areaFilter && areaFilter.length > 0
        ? slotAreas.filter((a) => areaFilter.includes(a.id))
        : slotAreas;
      const bookableAreas = filteredSlotAreas.filter((a) => a.available);
      const areaStepRequired =
        isAreaMode && filteredSlotAreas.length > 0 && bookableAreas.length !== 1;

      if (isAreaMode) {
        if (bookableAreas.length === 1) {
          const area = bookableAreas[0];
          setBookingSelectedArea({ id: String(area.id), name: area.name });
        } else {
          setBookingSelectedArea(null);
        }
      }

      // Determine if EXPERIENCE step should be shown. We must compute locally
      // because goToNext() has a stale closure — booking.selectedTime hasn't
      // committed yet, so the flow context would see empty selectedSlotMenus.
      // Predicate is shared with steps.ts EXPERIENCE.isRequired so the two
      // call sites can never drift.
      const singleMenu = enabledMenus.length === 1 ? enabledMenus[0] : null;
      const singleHasGuarantee = !!(singleMenu &&
        isPaymentGuarantee(singleMenu.guarantee?.type, singleMenu.guarantee?.fee));
      const singleHasPrice = !!(singleMenu && singleMenu.price && singleMenu.price.trim() !== '');
      const singleIsEvent = !!(singleMenu && singleMenu.eventMenu);
      const needsExperience = shouldShowExperience({
        isWaitingList: booking.flowType === 'waiting_list',
        preSelectedMenuId: booking.preSelectedMenuId ?? null,
        forceShowMenuSelection: !!restaurant.forceShowMenuSelection,
        menuCount: enabledMenus.length,
        singleMenuHasGuarantee: singleHasGuarantee,
        singleMenuHasPrice: singleHasPrice,
        singleMenuIsEvent: singleIsEvent,
        // isSpecialDay not derivable from slot data alone at this point;
        // goToNext() fallback uses the real FlowContext which has it.
        isSpecialDay: false,
      });

      // Pre-assign experience for single menu; clear for multiple so user picks.
      // In area-mode we don't auto-assign area from menu.area — that path conflicts
      // with the slot's availableAreas selection above.
      if (enabledMenus.length >= 2) {
        setSelectedExperience(null);
        if (!isAreaMode) setBookingSelectedArea(null);
      } else if (enabledMenus.length === 1) {
        const menu = enabledMenus[0];
        setSelectedExperience({ id: String(menu.id), title: menu.name });
        if (!isAreaMode && menu.area) {
          setBookingSelectedArea({ id: String(menu.area), name: String(menu.area) });
        }
      }

      if (!authState.isAuthenticated) {
        redirectToAuth('login', {
          diners,
          selectedDate,
          service: service ?? effectiveService,
          selectedTime: time,
          flowType: booking.flowType,
          specialEventSlug: booking.specialEvent?.slug,
          isWaitingList: booking.flowType === 'waiting_list',
        });
      } else if (areaStepRequired) {
        navigate('/select-area');
      } else if (needsExperience) {
        navigate('/select-experience');
      } else {
        goToNext();
      }
    },
    [goToNext, navigate, setBookingSelectedTime, timeSlots, setSelectedExperience, setBookingSelectedArea, authState.isAuthenticated, authState.blockedInRestaurantsId, restaurant, diners, selectedDate, service, effectiveService, booking.flowType, booking.specialEvent, booking.areaFilterIds, booking.selectedMenuFilter, booking.preSelectedMenuId]
  );

  /**
   * Back navigation — orchestrator determines the correct previous step
   */
  const handleBack = useCallback(() => {
    goToPrevious();
  }, [goToPrevious]);

  /**
   * Changes the selected experience and resets time
   */
  const changeExperience = useCallback((value: string) => {
    setSelectedExp(value);
    setSelectedTime(null);
    const experience = EXPERIENCES.find((exp) => exp.id === value);
    if (experience) {
      setSelectedExperience({ id: experience.id, title: experience.title });
    }
  }, [setSelectedExperience]);

  /**
   * Join waiting list: switch flow type and navigate to auth or WL form
   */
  const handleJoinWaitingList = useCallback(() => {
    // Extract menus from raw center slots (includes type "No" slots that processSlots filters out).
    // This replicates legacy behavior where WL menus come from the day's raw slots.
    const rawCenterSlots = effectiveService
      ? directSlotsByService[effectiveService]?.center?.slots ?? []
      : [];
    const menuMap = new Map<number, { id: number; name: string; eventMenu: boolean; orderPriority: number; allowPairings?: boolean; isPairingMandatory?: boolean }>();
    for (const slot of rawCenterSlots) {
      for (const m of slot.menus) {
        if (!menuMap.has(m.id)) {
          menuMap.set(m.id, { id: m.id, name: m.name, eventMenu: m.eventMenu, orderPriority: m.orderPriority ?? 0, allowPairings: m.allowPairings, isPairingMandatory: m.isPairingMandatory });
        }
      }
    }
    const wlSlotMenus = Array.from(menuMap.values()).sort((a, b) => {
      if (a.eventMenu && !b.eventMenu) return -1;
      if (!a.eventMenu && b.eventMenu) return 1;
      return a.orderPriority - b.orderPriority;
    });

    updateBooking({ flowType: 'waiting_list', wlSlotMenus });
    // Navigate directly because goToNext() would use the previous flowType pre-setState
    if (!authState.isAuthenticated) {
      redirectToAuth('login', {
        diners,
        selectedDate,
        service,
        selectedTime: booking.selectedTime,
        flowType: 'waiting_list',
        isWaitingList: true,
      });
    } else if (
      !authState.phone?.trim()
      || !authState.name?.trim()
    ) {
      navigate(COMPLETE_PROFILE_PATH, { state: { from: '/waiting-list' } });
    } else {
      navigate('/waiting-list');
    }
  }, [updateBooking, diners, selectedDate, service, booking.selectedTime, authState.isAuthenticated, authState.name, authState.phone, navigate, effectiveService, directSlotsByService]);

  /**
   * Preferred access login: navigate to sign-in with returnTo so user comes back to select-time
   */
  const handlePreferredLogin = useCallback(() => {
    redirectToAuth('login', {
      diners,
      selectedDate,
      service,
    });
  }, [diners, selectedDate, service]);

  /**
   * Get the label for the selected experience
   */
  const getSelectedExperienceLabel = useCallback(() => {
    const experience = EXPERIENCES.find((exp) => exp.id === selectedExp);
    return experience ? experience.title : '';
  }, [selectedExp]);

  /**
   * Convert API slots to UI TimeSlot format.
   * When a menu filter is active (selectedMenuFilter), only show slots
   * that contain at least one of the selected menus. This avoids re-fetching
   * search-all-hours — the full slots stay in booking.timeSlots and we
   * filter on the frontend.
   */
  const menuFilter = booking.selectedMenuFilter;
  const areaFilterIds = booking.areaFilterIds;
  const isAreaMode = restaurant.publicSearchMode === PUBLIC_SEARCH_MODE.AREA;
  const availableTimes = useMemo((): TimeSlot[] => {
    if (!timeSlots || timeSlots.length === 0) return [];

    const filterSet = menuFilter && menuFilter.length > 0
      ? new Set(menuFilter)
      : null;
    const areaFilterSet = areaFilterIds && areaFilterIds.length > 0
      ? new Set(areaFilterIds)
      : null;

    return timeSlots
      .filter((slot) => {
        if (!filterSet) return true;
        return slot.menus.some((m) => !m.disabled && filterSet.has(m.id));
      })
      .filter((slot) => {
        // In area-mode, hide slots whose areas are all sold-out so the user
        // never lands on /select-area with nothing selectable. When an area
        // filter is active, scope the sold-out check to the filter's areas.
        if (!isAreaMode) return true;
        const areas = slot.availableAreas;
        if (!areas || areas.length === 0) return true;
        const candidates = areaFilterSet
          ? areas.filter((a) => areaFilterSet.has(a.id))
          : areas;
        if (candidates.length === 0) return false;
        return candidates.some((a) => a.available);
      })
      .map((slot) => ({
        time: slot.hour,
        isPremium: slot.preferred,
        deposit: slot.deposit,
        type: slot.type,
        hasEventMenu: slot.menus?.some((m) => m.eventMenu && !m.disabled) ?? false,
      }));
  }, [timeSlots, menuFilter, isAreaMode, areaFilterIds]);

  // Derive alternative menus when pre-selected menu has no availability
  // but other menus do have slots for this date/service/party-size.
  // Excludes both the original pre-selected menu AND the currently filtered menu
  // (in case the user already switched to an alternative that also has no dispo).
  const alternativeMenus = useMemo(() => {
    if (availableTimes.length > 0 || !booking.preSelectedMenuId || !timeSlots || timeSlots.length === 0) return [];

    const excludeIds = new Set<number>();
    excludeIds.add(booking.preSelectedMenuId);
    if (menuFilter) {
      for (const id of menuFilter) excludeIds.add(id);
    }

    // First pass: collect menu metadata
    const menuMap = new Map<number, { id: number; name: string; image?: string; description?: string; details?: string; price?: string; times: string[] }>();

    for (const slot of timeSlots) {
      for (const menu of slot.menus) {
        if (menu.disabled || excludeIds.has(menu.id)) continue;
        if (menu.eventMenu || menu.marketplace || menu.preferredAccess) continue;

        if (!menuMap.has(menu.id)) {
          menuMap.set(menu.id, {
            id: menu.id,
            name: menu.name,
            image: (menu as Record<string, unknown>).image as string | undefined,
            description: menu.description,
            details: menu.details,
            price: menu.price,
            times: [],
          });
        }
        menuMap.get(menu.id)!.times.push(slot.hour);
      }
    }

    return Array.from(menuMap.values());
  }, [availableTimes.length, booking.preSelectedMenuId, menuFilter, timeSlots]);

  // Derive cross-service alternatives when selected service has no slots
  const crossServiceAlternatives = useMemo((): CrossServiceAlternative[] => {
    if (availableTimes.length > 0 || isAllSlotsLoading || !service || allServiceTypes.length <= 1) return [];

    return allServiceTypes
      .filter((s) => s !== service)
      .map((s) => {
        const slots = allGetServiceSlots(s);
        return {
          service: s,
          slots: slots.map((slot) => ({
            time: slot.hour,
            isPremium: slot.preferred,
            deposit: slot.deposit,
            type: slot.type,
            hasEventMenu: slot.menus?.some((m) => m.eventMenu && !m.disabled) ?? false,
          })),
        };
      })
      .filter((alt) => alt.slots.length > 0);
  }, [availableTimes.length, isAllSlotsLoading, service, allServiceTypes, allGetServiceSlots]);

  /**
   * Select a slot from a cross-service alternative.
   * Switches the booking service, loads the new service's slots,
   * auto-assigns menu + area, and advances the flow.
   */
  const handleSelectCrossServiceSlot = useCallback(
    (altService: ServiceType, time: string) => {
      // Blocked user check
      if (
        authState.isAuthenticated &&
        authState.blockedInRestaurantsId?.includes(String(restaurant.id))
      ) {
        setLimitWarning({
          open: true,
          type: 'blocked',
          limit: 0,
          contactEmail: restaurant.contactEmail,
        });
        return;
      }

      // Get slots and response for the new service
      const newSlots = allGetServiceSlots(altService);
      const response = allSlotsByService[altService];

      // Check slot-level limits
      const slot = newSlots.find((s) => s.hour === time);
      if (slot?.type === 'daily-limit-reached') {
        setLimitWarning({ open: true, type: 'daily', limit: restaurant.dailyLimit, contactEmail: restaurant.contactEmail });
        return;
      }
      if (slot?.type === 'weekly-limit-reached') {
        setLimitWarning({ open: true, type: 'weekly', limit: restaurant.weeklyLimit, contactEmail: restaurant.contactEmail });
        return;
      }
      if (slot?.type === 'monthly-limit-reached') {
        setLimitWarning({ open: true, type: 'monthly', limit: restaurant.monthlyLimit, contactEmail: restaurant.contactEmail });
        return;
      }

      // Switch service and update slots in booking state
      setBookingService(altService);
      setBookingTimeSlots(newSlots);
      if (response) {
        setBookingSlotTerms(response.center?.termsAndConditions ?? []);
        setBookingColumnType(response.center?.type ?? null);

        // Detect preferred-only column for the new service
        const rawSlots = response.center?.slots ?? [];
        const hasPreferred = response.center?.hasPreferred;
        const thereArePreferred = rawSlots.some((s) => s.preferred && s.type !== 'No');
        const thereAreNormal = rawSlots.some((s) => !s.preferred && s.type !== 'No');
        updateBooking({ centerHasOnlyPreferred: !!hasPreferred && thereArePreferred && !thereAreNormal });
      }

      // Set selected time
      setSelectedTime(time);
      setBookingSelectedTime(time);

      // Auto-assign menu + area, respecting menu filter (same logic as handleSelectTime)
      const allCrossEnabledMenus = slot?.menus?.filter((m) => !m.disabled) ?? [];
      const crossFilter = booking.selectedMenuFilter;
      const crossFilterSet = crossFilter && crossFilter.length > 0
        ? new Set(crossFilter)
        : null;
      const enabledMenus = crossFilterSet
        ? allCrossEnabledMenus.filter((m) => crossFilterSet.has(m.id))
        : allCrossEnabledMenus;

      // Area routing (Flow C/D): mirror handleSelectTime behavior.
      const isAreaMode = restaurant.publicSearchMode === PUBLIC_SEARCH_MODE.AREA;
      const slotAreas = slot?.availableAreas ?? [];
      const areaFilter = booking.areaFilterIds;
      const filteredSlotAreas = areaFilter && areaFilter.length > 0
        ? slotAreas.filter((a) => areaFilter.includes(a.id))
        : slotAreas;
      const bookableAreas = filteredSlotAreas.filter((a) => a.available);
      const areaStepRequired =
        isAreaMode && filteredSlotAreas.length > 0 && bookableAreas.length !== 1;

      if (isAreaMode) {
        if (bookableAreas.length === 1) {
          const area = bookableAreas[0];
          setBookingSelectedArea({ id: String(area.id), name: area.name });
        } else {
          setBookingSelectedArea(null);
        }
      }

      // Same predicate as handleSelectTime — shared with steps.ts.
      const crossSingleMenu = enabledMenus.length === 1 ? enabledMenus[0] : null;
      const crossSingleGuarantee = !!(crossSingleMenu &&
        isPaymentGuarantee(crossSingleMenu.guarantee?.type, crossSingleMenu.guarantee?.fee));
      const crossSingleHasPrice = !!(crossSingleMenu && crossSingleMenu.price && crossSingleMenu.price.trim() !== '');
      const crossSingleIsEvent = !!(crossSingleMenu && crossSingleMenu.eventMenu);
      const needsExperience = shouldShowExperience({
        isWaitingList: booking.flowType === 'waiting_list',
        preSelectedMenuId: booking.preSelectedMenuId ?? null,
        forceShowMenuSelection: !!restaurant.forceShowMenuSelection,
        menuCount: enabledMenus.length,
        singleMenuHasGuarantee: crossSingleGuarantee,
        singleMenuHasPrice: crossSingleHasPrice,
        singleMenuIsEvent: crossSingleIsEvent,
        isSpecialDay: false,
      });

      if (enabledMenus.length >= 2) {
        setSelectedExperience(null);
        if (!isAreaMode) setBookingSelectedArea(null);
      } else if (enabledMenus.length === 1) {
        const menu = enabledMenus[0];
        setSelectedExperience({ id: String(menu.id), title: menu.name });
        if (!isAreaMode && menu.area) {
          setBookingSelectedArea({ id: String(menu.area), name: String(menu.area) });
        }
      }

      if (!authState.isAuthenticated) {
        redirectToAuth('login', {
          diners,
          selectedDate,
          service: altService,
          selectedTime: time,
          flowType: booking.flowType,
          specialEventSlug: booking.specialEvent?.slug,
          isWaitingList: booking.flowType === 'waiting_list',
        });
      } else if (areaStepRequired) {
        navigate('/select-area');
      } else if (needsExperience) {
        navigate('/select-experience');
      } else {
        goToNext();
      }
    },
    [authState, restaurant, allGetServiceSlots, allSlotsByService, setBookingService, updateBooking, setBookingTimeSlots, setBookingSlotTerms, setBookingColumnType, setSelectedTime, setBookingSelectedTime, setSelectedExperience, setBookingSelectedArea, navigate, goToNext, diners, selectedDate, booking.flowType, booking.specialEvent, booking.areaFilterIds, booking.selectedMenuFilter, booking.preSelectedMenuId],
  );

  // Select an alternative menu's time slot directly.
  // Updates the filter to the new menu, finds the slot, auto-assigns menu/area, and advances.
  const handleSelectAlternativeMenu = useCallback((menuId: number, time?: string) => {
    updateBooking({ selectedMenuFilter: [menuId] });
    if (!time) return;

    const slot = timeSlots?.find((s) => s.hour === time);
    if (!slot) return;

    setSelectedTime(time);
    setBookingSelectedTime(time);

    const altIsAreaMode = restaurant.publicSearchMode === PUBLIC_SEARCH_MODE.AREA;
    const altRawSlotAreas = slot.availableAreas ?? [];
    const altAreaFilter = booking.areaFilterIds;
    const altSlotAreas = altAreaFilter && altAreaFilter.length > 0
      ? altRawSlotAreas.filter((a) => altAreaFilter.includes(a.id))
      : altRawSlotAreas;
    const altBookableAreas = altSlotAreas.filter((a) => a.available);
    const altAreaStepRequired =
      altIsAreaMode && altSlotAreas.length > 0 && altBookableAreas.length !== 1;

    // Auto-assign the specific menu from this slot
    const targetMenu = slot.menus.find((m) => m.id === menuId && !m.disabled);
    if (targetMenu) {
      setSelectedExperience({ id: String(targetMenu.id), title: targetMenu.name });
    }

    if (altIsAreaMode) {
      if (altBookableAreas.length === 1) {
        const area = altBookableAreas[0];
        setBookingSelectedArea({ id: String(area.id), name: area.name });
      } else {
        setBookingSelectedArea(null);
      }
    } else if (targetMenu?.area) {
      setBookingSelectedArea({ id: String(targetMenu.area), name: String(targetMenu.area) });
    }

    if (!authState.isAuthenticated) {
      redirectToAuth('login', {
        diners,
        selectedDate,
        service: service ?? effectiveService,
        selectedTime: time,
        flowType: booking.flowType,
        specialEventSlug: booking.specialEvent?.slug,
        isWaitingList: booking.flowType === 'waiting_list',
      });
    } else if (altAreaStepRequired) {
      navigate('/select-area');
    } else {
      goToNext();
    }
  }, [updateBooking, timeSlots, setBookingSelectedTime, setSelectedExperience, setBookingSelectedArea, authState.isAuthenticated, goToNext, navigate, diners, selectedDate, service, effectiveService, booking.flowType, booking.specialEvent, restaurant.publicSearchMode, booking.areaFilterIds]);

  // Build partner suggestions from API data + restaurant.partners
  const partnerSuggestions = useMemo((): PartnerSuggestion[] => {
    if (!hasPartners || !partnerAvailabilityData) return [];

    return (restaurant.partners || [])
      .filter((p) => p.subdomainPrefix) // only partners with a bookable subdomain
      .map((partner) => {
        const entry = partnerAvailabilityData[partner.id];
        const slots = entry?.availability?.[0]?.hours?.center?.slots ?? [];
        const hasAvailability = slots.some((s) => s.type !== 'No');
        return { partner, hasAvailability };
      });
  }, [hasPartners, partnerAvailabilityData, restaurant.partners]);

  // Combine UI loading skeleton with actual slot loading
  const isActuallyLoading = loading || (needsDirectLoad && isDirectSlotsLoading);

  return {
    showExperience,
    selectedExp,
    setSelectedExp: changeExperience,
    selectedTime,
    setSelectedTime,
    loading: isActuallyLoading,
    EXPERIENCES,
    availableTimes,
    handleSelectTime,
    handleBack,
    getSelectedExperienceLabel,
    handleJoinWaitingList,
    // Per-service calendar day data (legacy home.js:3172-3173): different services on the
    // same day can have different types (e.g. lunch=available, dinner=walk-in). The merged
    // day type is only a fallback when calendar data isn't loaded yet.
    ...(() => {
      const day = selectedDate ? getDateAvailability(selectedDate) : null;
      const serviceDay = effectiveService ? day?.services?.[effectiveService] ?? null : null;
      const serviceDayType = serviceDay?.type ?? null;
      const calType = serviceDayType ?? booking.selectedCalendarDayType ?? '';

      // columnType: the calendar day type wins when it signals WL or walk-in, because
      // search-all-hours returns a generic "no-results" in those cases.
      const columnType = (calType === 'waiting-list' || calType === 'walk-in')
        ? calType
        : booking.columnType || calType;

      // Walk-in / WL flags come straight from the calendar (legacy confía en el calendario).
      // They are mutually exclusive by backend construction — AvailabilitySearchService.php
      // only emits 'walk-in' when waiting-list is unavailable.
      // Use calType (with selectedCalendarDayType fallback) so direct-navigation cases
      // where effectiveService is momentarily null still resolve correctly.
      // Legacy does NOT suppress walk-in during reschedule (only WL is suppressed,
      // see home.js:425), so no reschedule gate here.
      const isWalkInDay = calType === 'walk-in';

      // Mirror legacy allowWaitingList (home.js:425): disabled during reschedule.
      // Legacy reads waitingListFull from the per-service calendar row (home.js:3172);
      // fall back to the merged flag, then to "open" when calendar hasn't loaded yet.
      const isRescheduling = !!booking.rescheduleId;
      const serviceWlFull = serviceDay?.waitingListFull === '1';
      const mergedWlFull = day?.isWaitingListFull ?? false;
      const dayIsWlOpen = serviceDay
        ? !serviceWlFull
        : !mergedWlFull;
      const allowWaitingList = restaurant.allowWaitingList === true && !isRescheduling && dayIsWlOpen;
      const isWaitingListDay = allowWaitingList && calType === 'waiting-list';

      return { columnType, allowWaitingList, isWalkInDay, isWaitingListDay };
    })(),
    centerHasOnlyPreferred: booking.centerHasOnlyPreferred,
    isAuthenticated: authState.isAuthenticated,
    handlePreferredLogin,
    limitWarning,
    closeLimitWarning,
    crossServiceAlternatives,
    handleSelectCrossServiceSlot,
    calendarAvailability,
    isCalendarLoading,
    handleCalendarDateSelect,
    showNoAvailabilityCalendar: !isActuallyLoading && availableTimes.length === 0,
    calendarServiceTypes: allServiceTypes,
    currentService: service,
    partnerSuggestions,
    alternativeMenus,
    handleSelectAlternativeMenu,
  };
}
